import { useState } from "react";
import GenerateReport from "./GenerateReport";

// ═══════════════════════════════════════════════════════
// MATRICE DE SCORING — QUESTIONNAIRE D — 15-17 ANS
// 90% ADO + 10% PARENT
// ═══════════════════════════════════════════════════════


const PROFILES = {
  INT: {
    name: "L'Athlète accompli",
    emoji: "⚡",
    tagline: "L'effort physique intense est sa vérité — pas une contrainte",
    description: "Cet adolescent se réalise par le dépassement physique total. L'entraînement intensif est pour lui une source d'énergie et d'équilibre. Il cherche à se surpasser constamment, aime mesurer ses progrès, et trouve dans l'effort physique une forme de méditation active.",
    color: "#E8732A", bg: "#FFF0E6",
    top: [
      { emoji: "🏊", name: "Natation compétition régionale/nationale", why: "Effort pur, progression au centième de seconde, discipline de vie" },
      { emoji: "🏃", name: "Athlétisme — demi-fond ou sprint", why: "Se dépasser, battre son chrono, compétitions régulières" },
      { emoji: "🥊", name: "Boxe ou sports de combat compétition", why: "Effort total, mental d'acier, résultats mesurables" },
      { emoji: "🚴", name: "Cyclisme ou triathlon junior", why: "Endurance, dépassement de soi, courses régulières" },
    ],
    explore: ["Escalade compétition", "Crossfit / musculation", "Rugby compétition", "Ski compétition"],
    avoid: "Les activités artistiques ou créatives sans composante physique intense — musique, théâtre, arts plastiques.",
  },
  PRE: {
    name: "Le Virtuose",
    emoji: "🎯",
    tagline: "La maîtrise absolue est sa quête — le détail le fascine",
    description: "Cet adolescent est habité par la perfection technique. Il peut consacrer des heures à affiner un geste, une technique, un détail. La progression mesurable et la maîtrise progressive sont ses sources de satisfaction les plus profondes.",
    color: "#3D2B8E", bg: "#EDE8FF",
    top: [
      { emoji: "⚔️", name: "Escrime niveau régional/national", why: "Précision absolue, stratégie, championnats de France accessibles" },
      { emoji: "🎵", name: "Musique — niveau conservatoire", why: "Maîtrise technique poussée, récitals, excellence musicale" },
      { emoji: "🏹", name: "Tir à l'arc compétition nationale", why: "Concentration maximale, geste parfait, classements nationaux" },
      { emoji: "🥋", name: "Arts martiaux — passage ceinture noire", why: "Maîtrise technique de haut niveau, grades, discipline de vie" },
    ],
    explore: ["Golf compétition", "Équitation dressage compétition", "Tennis classement national", "Tir sportif"],
    avoid: "Les activités trop libres ou collectives où la technique individuelle est secondaire.",
  },
  EXP: {
    name: "L'Artiste affirmé",
    emoji: "🎨",
    tagline: "Créer n'est pas un loisir — c'est une nécessité vitale",
    description: "Cet adolescent a une relation viscérale à la création et à l'expression. L'activité artistique n'est pas un hobby — c'est une façon d'être et de comprendre le monde. Il a besoin d'un espace de création totale, de liberté, et de pouvoir laisser une trace.",
    color: "#9B4FBB", bg: "#F5E8FF",
    top: [
      { emoji: "🎵", name: "Musique — composition et groupe", why: "Créer ses propres morceaux, se produire, laisser une trace musicale" },
      { emoji: "💃", name: "Danse contemporaine ou hip-hop avancé", why: "Expression corporelle totale, chorégraphies personnelles, spectacles" },
      { emoji: "🎭", name: "Théâtre — textes exigeants et mise en scène", why: "Expression totale, liberté d'interprétation, travail en profondeur" },
      { emoji: "🎨", name: "Arts plastiques / photographie artistique", why: "Créer quelque chose d'unique, s'exprimer visuellement, exposer" },
    ],
    explore: ["Cinéma / réalisation vidéo", "Écriture créative", "Street art légal", "Capoeira avancé"],
    avoid: "Les activités très codifiées sans espace créatif — athlétisme pur, natation technique sans aspect artistique.",
  },
  COM: {
    name: "Le Guerrier compétitif",
    emoji: "⚔️",
    tagline: "La victoire n'est pas tout — mais perdre sans se battre, c'est inacceptable",
    description: "Cet adolescent est animé par un feu compétitif profond. Il a besoin d'un adversaire à hauteur, d'un classement à grimper, d'une victoire à conquérir. La compétition n'est pas un stress — c'est un état naturel qui le révèle.",
    color: "#C62828", bg: "#FFEBEE",
    top: [
      { emoji: "♟️", name: "Échecs — tournois nationaux / ELO", why: "Compétition mentale pure, classement ELO, championnats de France" },
      { emoji: "🥊", name: "Sports de combat compétition nationale", why: "Duel pur, se dépasser face à l'adversaire, titres régionaux" },
      { emoji: "⚔️", name: "Escrime compétition nationale", why: "Duel élégant et technique, classements nationaux, championnats" },
      { emoji: "🎾", name: "Tennis classement national FFT", why: "Duel individuel, classement officiel, progrès mesurables" },
    ],
    explore: ["E-sport compétition", "Badminton compétition", "Squash", "Volley compétition"],
    avoid: "Les activités sans classement ni enjeu compétitif clair — danse libre, théâtre, arts plastiques.",
  },
  COL: {
    name: "Le Pilier collectif",
    emoji: "🤝",
    tagline: "Seul on va plus vite — ensemble on va plus loin",
    description: "Cet adolescent tire son énergie du groupe. Les liens forts construits dans une équipe, les victoires partagées, la cohésion d'une troupe — c'est ce qui lui donne envie de se lever le matin pour s'entraîner. Son talent pour fédérer est rare.",
    color: "#1B6B3A", bg: "#E8F5E9",
    top: [
      { emoji: "⚽", name: "Football en club compétition régionale", why: "Collectif soudé, victoires partagées, engagement total dans l'équipe" },
      { emoji: "🏀", name: "Basketball compétition régionale", why: "Interdépendance maximale, esprit d'équipe fort, jeu rapide" },
      { emoji: "🎭", name: "Troupe de théâtre avancée", why: "Création collective intense, liens humains forts, spectacles aboutis" },
      { emoji: "🎵", name: "Groupe de musique ou orchestre", why: "Cohésion musicale, création collective, concerts ensemble" },
    ],
    explore: ["Handball compétition", "Volleyball", "Aviron en équipe", "Comédie musicale avancée"],
    avoid: "Les activités strictement individuelles — tir à l'arc, natation individuelle, échecs, musique en solo.",
  },
  SCE: {
    name: "Le Star en devenir",
    emoji: "🎭",
    tagline: "Le public ne le stresse pas — il le transcende",
    description: "Cet adolescent est fait pour être regardé. Devant un public, il donne une version de lui-même que personne ne connaît autrement. Les représentations ne sont pas des sources de stress — elles sont des sources d'énergie et de révélation personnelle.",
    color: "#1565C0", bg: "#E3F2FD",
    top: [
      { emoji: "🎭", name: "Théâtre — rôles principaux et mises en scène", why: "Performance pure, public, représentations importantes" },
      { emoji: "💃", name: "Danse de scène compétition", why: "Public, compétitions, battles — être regardé et applaudi" },
      { emoji: "🎵", name: "Musique — concerts et scènes publiques", why: "Se produire devant les autres, maîtrise visible, être applaudi" },
      { emoji: "🎪", name: "Arts du cirque — numéros élaborés", why: "Performance totale, public, numéros personnels aboutis" },
    ],
    explore: ["Comédie musicale professionnalisante", "Danse hip-hop battles", "Stand-up / humour", "Magie avancée"],
    avoid: "Les activités sans représentation publique — natation, athlétisme en solitaire, échecs.",
  },
};


const ADO_QUESTIONS = [
  {
    id: "A1",
    text: "Qu'est-ce qui t'a fait décrocher d'une activité que tu aimais au départ ?",
    consigne: "Choisis les 2 raisons les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "La répétition mécanique — j'ai besoin de renouvellement constant", scores: { EXP: 1, INT: 1 } },
      { id: "B", text: "La pression compétitive ou le regard des autres — ça m'écrase", scores: { COL: 1, EXP: 1 } },
      { id: "C", text: "Le manque de liberté — on m'imposait une façon de faire qui ne me ressemblait pas", scores: { EXP: 2 } },
      { id: "D", text: "La lenteur des progrès — j'ai besoin de voir que j'avance rapidement", scores: { INT: 1, COM: 1 } },
      { id: "E", text: "L'ambiance ou les personnes — ça ne matchait pas du tout", scores: { COL: 1 } },
      { id: "F", text: "Je n'ai jamais abandonné une activité que j'aimais profondément", scores: { PRE: 1 } },
    ],
  },
  {
    id: "A2",
    text: "Comment tu vis réellement la compétition ?",
    consigne: "Une seule réponse — sois honnête",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Elle me transcende — je me surpasse quand il y a un vrai enjeu", scores: { COM: 3 } },
      { id: "B", text: "Je l'accepte mais je dois gérer le stress — elle n'est pas naturelle pour moi", scores: { COM: 1 } },
      { id: "C", text: "Elle me perturbe — je préfère nettement les contextes sans classement", scores: { EXP: 1, COL: 1 } },
      { id: "D", text: "Je la recherche mais la supporte mal — j'aime l'idée mais pas la réalité", scores: { COM: 1 } },
    ],
  },
  {
    id: "A3",
    text: "Tes 2 priorités dans une activité",
    consigne: "Choisis les 2 plus importantes pour toi",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Me dépasser moi-même et progresser", scores: { PRE: 2 } },
      { id: "B", text: "Être reconnu et obtenir des résultats visibles", textF: "Être reconnue et obtenir des résultats visibles", scores: { COM: 2 } },
      { id: "C", text: "M'exprimer librement et créer quelque chose d'unique", scores: { EXP: 2 } },
      { id: "D", text: "Appartenir à un groupe et partager des moments forts", scores: { COL: 2 } },
      { id: "E", text: "Maîtriser une technique complexe dans ses moindres détails", scores: { PRE: 2 } },
    ],
  },
  {
    id: "A4",
    text: "Décris ton rapport à l'effort physique honnêtement",
    consigne: "Choisis les 2 réponses les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "L'effort intense me donne de l'énergie — transpirer et me dépasser me fait du bien", scores: { INT: 3 } },
      { id: "B", text: "Je préfère l'effort technique et précis — la maîtrise du geste plus que l'intensité", scores: { PRE: 3 } },
      { id: "C", text: "L'effort expressif me correspond — bouger avec fluidité et sens plutôt que force brute", scores: { EXP: 3 } },
      { id: "D", text: "L'effort physique n'est pas ce qui me motive — les aspects créatifs ou mentaux priment", scores: { EXP: 2 } },
    ],
  },
  {
    id: "A5",
    text: "Tu préfères travailler seul ou en groupe ?",
    consigne: "Une seule réponse — la plus honnête",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Seul — je suis plus efficace, concentré et créatif sans les autres", textF: "Seule — je suis plus efficace, concentrée et créative sans les autres", scores: { PRE: 2 } },
      { id: "B", text: "En duo ou petit groupe de confiance — les liens forts priment sur le nombre", scores: { COL: 1 } },
      { id: "C", text: "En grande équipe — j'ai besoin de la dynamique collective pour me dépasser", scores: { COL: 3 } },
      { id: "D", text: "Ça dépend — seul pour travailler, en groupe pour performer", textF: "Ça dépend — seule pour travailler, en groupe pour performer", scores: { SCE: 1, PRE: 1 } },
    ],
  },
  {
    id: "A6",
    text: "Ton rapport à la scène et au public ?",
    consigne: "Une seule réponse — la plus honnête",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Je m'élève devant le public — c'est là que je donne le meilleur de moi-même", scores: { SCE: 3 } },
      { id: "B", text: "J'apprécie d'être regardé mais ça ne change pas fondamentalement ma performance", textF: "J'apprécie d'être regardée mais ça ne change pas fondamentalement ma performance", scores: { SCE: 1 } },
      { id: "C", text: "Je préfère pratiquer sans public — le regard des autres me déconcentre", scores: { PRE: 1 } },
      { id: "D", text: "L'idée du public me stresse ou me paralyse — je fonctionne mieux dans l'anonymat", scores: { PRE: 2 } },
    ],
  },
  {
    id: "A7",
    text: "Quand tu rates quelque chose d'important — comment tu réagis vraiment ?",
    consigne: "Choisis les 2 réponses les plus honnêtes",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Je reprends rapidement — l'échec ne m'affecte pas longtemps", scores: { INT: 1 } },
      { id: "B", text: "Je l'analyse froidement pour comprendre et ne pas recommencer", scores: { PRE: 2 } },
      { id: "C", text: "L'échec me ronge pendant un moment — j'ai du mal à décrocher", scores: { COM: 1 } },
      { id: "D", text: "Je me remets en question sur le fond — est-ce que cette activité est vraiment pour moi ?", scores: { EXP: 1 } },
      { id: "E", text: "J'ai besoin d'être soutenu ou encouragé avant de reprendre", textF: "J'ai besoin d'être soutenue ou encouragée avant de reprendre", scores: { COL: 1 } },
    ],
  },
  {
    id: "A8",
    text: "Quel type d'entraîneur ou de professeur fonctionne le mieux avec toi ?",
    consigne: "Choisis les 2 réponses les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Exigeant et direct — il me pousse dans mes retranchements et je progresse", scores: { COM: 1, PRE: 1 } },
      { id: "B", text: "Bienveillant et à l'écoute — la relation humaine est essentielle à ma progression", scores: { COL: 1, EXP: 1 } },
      { id: "C", text: "Qui me laisse de l'autonomie — je n'aime pas être trop encadré", textF: "Qui me laisse de l'autonomie — je n'aime pas être trop encadrée", scores: { EXP: 2 } },
      { id: "D", text: "Pédagogue et technique — qui m'explique le pourquoi de chaque exercice", scores: { PRE: 2 } },
    ],
  },
  {
    id: "A9",
    text: "Ton rapport aux règles dans une activité ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "J'ai besoin de cadre — les règles claires me rassurent et me donnent des repères", scores: { PRE: 1, COL: 1 } },
      { id: "B", text: "J'accepte les règles techniques qui ont un sens, mais je refuse les règles arbitraires", scores: { PRE: 1 } },
      { id: "C", text: "Je préfère les activités avec peu de règles — la liberté me permet d'exprimer mon potentiel", scores: { EXP: 2 } },
      { id: "D", text: "Je crée mes propres règles — j'ai naturellement tendance à innover", scores: { EXP: 2 } },
    ],
  },
  {
    id: "A10",
    text: "L'aspect artistique ou créatif compte-t-il pour toi ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Oui, c'est essentiel — créer, m'exprimer, apporter ma touche est fondamental", scores: { EXP: 3 } },
      { id: "B", text: "Oui, c'est un plus — mais la performance ou la maîtrise technique prime", scores: { PRE: 1, EXP: 1 } },
      { id: "C", text: "Non, c'est secondaire — la précision, la force ou la stratégie m'importent davantage", scores: { COM: 1, INT: 1 } },
    ],
  },
  {
    id: "A11",
    text: "Pratiques-tu des activités seul, hors de tout club ou cours organisé ?",
    consigne: "Choisis les réponses qui s'appliquent",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Oui — le sport seul (running, skate, vélo, musculation...)", textF: "Oui — le sport seule (running, skate, vélo, musculation...)", scores: { INT: 2 } },
      { id: "B", text: "Oui — la création seule (musique, art, écriture, photo, vidéo...)", textF: "Oui — la création seule (musique, art, écriture, photo, vidéo...)", scores: { EXP: 2 } },
      { id: "C", text: "Oui — les deux", scores: { INT: 1, EXP: 1 } },
      { id: "D", text: "Non — j'ai besoin d'un cadre organisé pour pratiquer régulièrement", scores: { COL: 1, PRE: 1 } },
    ],
  },
  {
    id: "A12",
    text: "Comment les gens qui te connaissent bien décriraient ta façon de pratiquer une activité ?",
    consigne: "Choisis les 2 descriptions les plus proches",
    multi: true, max: 2,
    options: [
      { id: "A", text: `“Il se dépense sans compter — il n'a pas de demi-mesure”`, textF: `“Elle se dépense sans compter — elle n'a pas de demi-mesure”`, scores: { INT: 2 } },
      { id: "B", text: `“Il est dans sa bulle — il peut pratiquer des heures sans voir le temps passer”`, textF: `“Elle est dans sa bulle — elle peut pratiquer des heures sans voir le temps passer”`, scores: { PRE: 2, EXP: 1 } },
      { id: "C", text: `“Il a besoin d'audience — il se transcende quand il est regardé”`, textF: `“Elle a besoin d'audience — elle se transcende quand elle est regardée”`, scores: { SCE: 2 } },
      { id: "D", text: `“Il est perfectionniste — il ne supporte pas de mal faire”`, textF: `“Elle est perfectionniste — elle ne supporte pas de mal faire”`, scores: { PRE: 2 } },
      { id: "E", text: `“Il est sociable dans son activité — l'ambiance compte autant que la pratique”`, textF: `“Elle est sociable dans son activité — l'ambiance compte autant que la pratique”`, scores: { COL: 2 } },
    ],
  },
  {
    id: "A13",
    text: "Qu'est-ce que tu cherches dans une activité que tu n'as pas encore trouvé ?",
    consigne: "Choisis les 2 réponses les plus proches",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Un niveau de compétition plus élevé — des adversaires vraiment à ma hauteur", scores: { COM: 2 } },
      { id: "B", text: "Plus de liberté créative — un espace pour exprimer vraiment ce que je ressens", scores: { EXP: 2 } },
      { id: "C", text: "Un groupe avec lequel je me sens vraiment en accord", scores: { COL: 2 } },
      { id: "D", text: "Un entraîneur qui me pousse vraiment à me dépasser", scores: { PRE: 1, INT: 1 } },
      { id: "E", text: "Des représentations ou performances publiques régulières", scores: { SCE: 2 } },
      { id: "F", text: "Je ne sais pas encore — je cherche toujours", scores: {} },
    ],
  },
  {
    id: "A14",
    text: "Si tu pouvais pratiquer n'importe quelle activité sans contrainte, laquelle choisirais-tu ?",
    consigne: "Choisis la réponse la plus proche",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Un sport de haut niveau à entraînement quotidien intensif", scores: { INT: 3, COM: 1 } },
      { id: "B", text: "Un art de maîtrise (musique, escrime, tir) au plus haut niveau technique", scores: { PRE: 3 } },
      { id: "C", text: "Une discipline artistique où je pourrais créer librement et me produire", scores: { EXP: 3, SCE: 1 } },
      { id: "D", text: "Un sport collectif dans une vraie équipe compétitive", scores: { COL: 3, COM: 1 } },
      { id: "E", text: "Une discipline de scène où je me produirais régulièrement en public", scores: { SCE: 3 } },
    ],
  },
  {
    id: "A15",
    text: "Qu'est-ce qui te différencie des autres dans une activité — ton vrai point fort ?",
    consigne: "Choisis les 2 réponses les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Mon intensité et ma capacité à souffrir — je ne lâche jamais", scores: { INT: 2 } },
      { id: "B", text: "Ma précision et mon attention aux détails — je vois ce que les autres ratent", scores: { PRE: 2 } },
      { id: "C", text: "Ma créativité et mon originalité — je fais les choses à ma façon", scores: { EXP: 2 } },
      { id: "D", text: "Mon mental compétitif — je me transcende face à l'adversité", scores: { COM: 2 } },
      { id: "E", text: "Ma capacité à fédérer et à élever le niveau du groupe autour de moi", scores: { COL: 2 } },
      { id: "F", text: "Ma présence et mon impact quand je performe devant les autres", scores: { SCE: 2 } },
    ],
  },
  {
    id: "A16",
    text: "Dans 3 ans, quelle relation veux-tu avoir avec une activité ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Pratiquer à haut niveau — entraînements quotidiens, compétitions nationales", scores: { COM: 2, INT: 1 } },
      { id: "B", text: "Avoir une vraie passion technique qui m'accompagne pour la vie", scores: { PRE: 2 } },
      { id: "C", text: "Créer et m'exprimer — que l'activité soit un moyen d'expression personnelle", scores: { EXP: 2 } },
      { id: "D", text: "Partager une passion avec un groupe — l'activité comme lien social fort", scores: { COL: 2 } },
      { id: "E", text: "Me produire régulièrement — que l'activité soit une scène d'expression publique", scores: { SCE: 2 } },
    ],
  },
  {
    id: "A17",
    text: "Y a-t-il une activité que tu n'as jamais osé essayer mais qui t'attire ?",
    consigne: "Choisis la réponse la plus proche",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Oui — un sport ou une discipline physique intensive que je n'ai pas eu l'occasion d'essayer", scores: { INT: 1 } },
      { id: "B", text: "Oui — une activité artistique ou créative que je juge parfois trop \"hors norme\"", scores: { EXP: 1 } },
      { id: "C", text: "Oui — une activité très compétitive qui me fait un peu peur", scores: { COM: 1 } },
      { id: "D", text: "Oui — une activité de scène ou de performance que j'admire de loin", scores: { SCE: 1 } },
      { id: "E", text: "Non — j'ai déjà essayé ce qui m'attire", scores: {} },
    ],
  },
  {
    id: "A18",
    text: "Ta phrase — celle qui te définit le mieux dans une activité",
    consigne: "Choisis les 2 phrases les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "\"Je ne m'entraîne pas pour participer — je m'entraîne pour gagner\"", scores: { COM: 3 } },
      { id: "B", text: "\"Je cherche la perfection — un geste parfait vaut plus que dix gestes moyens\"", scores: { PRE: 3 } },
      { id: "C", text: "\"Je crée — l'activité est mon moyen d'expression, pas une fin en soi\"", scores: { EXP: 3 } },
      { id: "D", text: `"Je me dépasse — l'effort intense est ma façon de me sentir vivant"`, textF: `"Je me dépasse — l'effort intense est ma façon de me sentir vivante"`, scores: { INT: 3 } },
      { id: "E", text: "\"Je fédère — le groupe est plus fort que la somme de ses individus\"", scores: { COL: 3 } },
      { id: "F", text: "\"Je performe — c'est devant les autres que je deviens vraiment moi-même\"", scores: { SCE: 3 } },
    ],
  },
];

const PARENT_QUESTIONS = [
  {
    id: "P1",
    text: "Y a-t-il une activité où vous avez vu votre enfant dans un état d'épanouissement évident ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Oui — une activité physique intensive ou sportive", scores: { INT: 2 } },
      { id: "B", text: "Oui — une activité artistique ou créative", scores: { EXP: 2 } },
      { id: "C", text: "Oui — une activité compétitive avec classement", scores: { COM: 2 } },
      { id: "D", text: "Oui — une activité collective avec des liens humains forts", scores: { COL: 2 } },
      { id: "E", text: "Oui — une activité de représentation devant un public", scores: { SCE: 2 } },
      { id: "F", text: "Non, je n'ai pas observé ce type de moment", scores: {} },
    ],
  },
  {
    id: "P2",
    text: "Selon vous, qu'est-ce que votre enfant sous-estime chez lui ?",
    consigne: "Cochez la réponse la plus proche",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Il sous-estime ses capacités physiques ou athlétiques", textF: "Elle sous-estime ses capacités physiques ou athlétiques", scores: { INT: 1 } },
      { id: "B", text: "Il sous-estime son talent artistique ou créatif", textF: "Elle sous-estime son talent artistique ou créatif", scores: { EXP: 1 } },
      { id: "C", text: "Il sous-estime son mental compétitif — il est plus fort qu'il ne le croit", textF: "Elle sous-estime son mental compétitif — elle est plus forte qu'elle ne le croit", scores: { COM: 1 } },
      { id: "D", text: "Il sous-estime son impact sur le groupe — il fédère naturellement", textF: "Elle sous-estime son impact sur le groupe — elle fédère naturellement", scores: { COL: 1 } },
      { id: "E", text: "Il sous-estime son aisance en public — il est fait pour la scène", textF: "Elle sous-estime son aisance en public — elle est faite pour la scène", scores: { SCE: 1 } },
      { id: "F", text: "Je ne vois pas de talent particulièrement sous-estimé", scores: {} },
    ],
  },
  {
    id: "P3",
    text: "Y a-t-il une information importante — passion cachée, expérience marquante — qui pourrait enrichir les recommandations ?",
    consigne: "Cochez la réponse la plus proche",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Oui — une passion physique ou sportive qu'il n'ose pas revendiquer", textF: "Oui — une passion physique ou sportive qu'elle n'ose pas revendiquer", scores: { INT: 1 } },
      { id: "B", text: "Oui — une passion artistique ou créative qu'il cache", textF: "Oui — une passion artistique ou créative qu'elle cache", scores: { EXP: 1 } },
      { id: "C", text: "Oui — une envie de compétition qu'il n'exprime pas", textF: "Oui — une envie de compétition qu'elle n'exprime pas", scores: { COM: 1 } },
      { id: "D", text: "Oui — un besoin de scène ou de reconnaissance publique qu'il minimise", textF: "Oui — un besoin de scène ou de reconnaissance publique qu'elle minimise", scores: { SCE: 1 } },
      { id: "E", text: "Non — ses réponses reflètent bien qui il est", textF: "Non — ses réponses reflètent bien qui elle est", scores: {} },
    ],
  },
];

function computeScores(adoAns, parAns) {
  const sc = { INT: 0, PRE: 0, EXP: 0, COM: 0, COL: 0, SCE: 0 };
  ADO_QUESTIONS.forEach(q => {
    const ans = adoAns[q.id] || [];
    ans.forEach(sel => {
      const o = q.options.find(x => x.id === sel);
      if (o) Object.entries(o.scores).forEach(([k, v]) => { sc[k] += v; });
    });
  });
  PARENT_QUESTIONS.forEach(q => {
    const ans = parAns[q.id] || [];
    ans.forEach(sel => {
      const o = q.options.find(x => x.id === sel);
      if (o) Object.entries(o.scores).forEach(([k, v]) => { sc[k] += v; });
    });
  });
  const total = Object.values(sc).reduce((a, b) => a + b, 0) || 1;
  const pct = {};
  Object.keys(sc).forEach(k => { pct[k] = Math.round((sc[k] / total) * 100); });
  const sorted = Object.entries(sc).sort((a, b) => b[1] - a[1]);
  return { sc, pct, primary: sorted[0][0], secondary: sorted[1][0] };
}

function applyGenderQ(text, gender) {
  if (!gender) return text;
  const isF = gender === "Fille";
  const map = {
    "Tu pr\u00e9f\u00e8res travailler seul ou en groupe ?": { G: "Tu pr\u00e9f\u00e8res travailler seul ou en groupe ?", F: "Tu pr\u00e9f\u00e8res travailler seule ou en groupe ?" },
    "Pratiques-tu des activit\u00e9s seul, hors de tout club ou cours organis\u00e9 ?": { G: "Pratiques-tu des activit\u00e9s seul, hors de tout club ou cours organis\u00e9 ?", F: "Pratiques-tu des activit\u00e9s seule, hors de tout club ou cours organis\u00e9 ?" },
    "Y a-t-il une activit\u00e9 o\u00f9 vous avez vu votre enfant dans un \u00e9tat d'\u00e9panouissement \u00e9vident ?": { G: "Y a-t-il une activit\u00e9 o\u00f9 vous avez vu votre fils dans un \u00e9tat d'\u00e9panouissement \u00e9vident ?", F: "Y a-t-il une activit\u00e9 o\u00f9 vous avez vu votre fille dans un \u00e9tat d'\u00e9panouissement \u00e9vident ?" },
    "Selon vous, qu'est-ce que votre enfant sous-estime chez lui ?": { G: "Selon vous, qu'est-ce que votre fils sous-estime chez lui ?", F: "Selon vous, qu'est-ce que votre fille sous-estime chez elle ?" },
  };
  if (map[text]) return isF ? map[text].F : map[text].G;
  return text;
}

export default function MatriceD({ onBack }) {
  const [step, setStep] = useState(0);
  const [aq, setAq] = useState(0);
  const [pq, setPq] = useState(0);
  const [adoAns, setAdoAns] = useState({});
  const [parAns, setParAns] = useState({});
  const [name, setName] = useState("");
  const [gender, setGender] = useState(""); // eslint-disable-line
  const [age, setAge] = useState("");
  const [res, setRes] = useState(null);

  function ansAdo(qId, oId, multi, max) {
    setAdoAns(p => {
      const cur = p[qId] || [];
      if (!multi || max === 1) return { ...p, [qId]: [oId] };
      if (cur.includes(oId)) return { ...p, [qId]: cur.filter(x => x !== oId) };
      if (cur.length >= max) return { ...p, [qId]: [...cur.slice(1), oId] };
      return { ...p, [qId]: [...cur, oId] };
    });
  }
  function ansPar(qId, oId, multi, max) {
    setParAns(p => {
      const cur = p[qId] || [];
      if (!multi || max === 1) return { ...p, [qId]: [oId] };
      if (cur.includes(oId)) return { ...p, [qId]: cur.filter(x => x !== oId) };
      if (cur.length >= max) return { ...p, [qId]: [...cur.slice(1), oId] };
      return { ...p, [qId]: [...cur, oId] };
    });
  }

  function nextA() { if (aq < ADO_QUESTIONS.length - 1) setAq(aq + 1); else setStep(2); }
  function prevA() { if (aq > 0) setAq(aq - 1); else setStep(0); }
  function nextP() {
    if (pq < PARENT_QUESTIONS.length - 1) setPq(pq + 1);
    else { setRes(computeScores(adoAns, parAns)); setStep(3); }
  }
  function prevP() { if (pq > 0) setPq(pq - 1); else setStep(1); }

  const V = "#1B4A6B", VM = "#2E6A9B", VP = "#D0E8F5", G = "#E8C87A";
  const btn = (c = V) => ({ background: c, color: "#fff", border: "none", borderRadius: 100, padding: "12px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer" });
  const bto = { background: "transparent", color: V, border: `2px solid ${VP}`, borderRadius: 100, padding: "10px 24px", fontSize: 14, cursor: "pointer" };
  const card = { background: "#fff", borderRadius: 20, padding: "28px 24px", maxWidth: 600, margin: "0 auto", boxShadow: "0 4px 24px rgba(27,74,107,0.08)" };
  const sm = { fontFamily: "system-ui", fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#4A8AB5", textTransform: "uppercase" };

  if (step === 0) return (
    <div style={{ background: "#F0F6FF", minHeight: "100vh", fontFamily: "system-ui" }}>
      <div style={{ background: `linear-gradient(135deg, ${V}, ${VM})`, padding: "28px 20px 24px", borderRadius: "0 0 24px 24px" }}>
        <div style={{ ...sm, color: "rgba(255,255,255,0.6)", marginBottom: 6 }}>Questionnaire D · 15-17 ans</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: "#fff", marginBottom: 4 }}>Quel est ton profil d'activité ?</div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}>90% rempli par toi — 10% par tes parents</div>
      </div>
      <div style={{ padding: 16 }}>
        <div style={card}>
          <div style={{ ...sm, marginBottom: 16 }}>Avant de commencer</div>
          <div style={{ fontSize: 14, color: "#1A1035", lineHeight: 1.7, marginBottom: 16 }}>
            À 15-17 ans, tu es le meilleur expert de toi-même. <b>Tu réponds seul à 18 questions</b> — sois honnête, pas ce que tu crois qu'on veut entendre.<br /><br />
            Tes parents répondent ensuite à <b>3 questions très courtes</b> uniquement pour apporter un regard extérieur.
          </div>
          <div style={{ background: "#FDF6E3", borderLeft: `4px solid ${G}`, padding: "12px 14px", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#6B5000", marginBottom: 24 }}>
            Ne montre pas tes réponses à tes parents — les deux regards indépendants donnent les meilleures recommandations.
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "#4A8AB5", marginBottom: 5 }}>Prénom</div>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Ton prénom..." style={{ width: "100%", padding: "10px 14px", border: `1.5px solid ${VP}`, borderRadius: 10, fontSize: 15, boxSizing: "border-box", fontFamily: "system-ui" }} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: "#4A8AB5", marginBottom: 5 }}>Âge</div>
            <input value={age} onChange={e => setAge(e.target.value)} placeholder="Ex: 16 ans" style={{ width: "50%", padding: "10px 14px", border: `1.5px solid ${VP}`, borderRadius: 10, fontSize: 15, fontFamily: "system-ui" }} />
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: "#8B7BB5", marginBottom: 8 }}>Votre enfant est...</div>
            <div style={{ display: "flex", gap: 12 }}>
              {["Fille", "Garçon"].map(g => (
                <div key={g} onClick={() => setGender(g)}
                  style={{ flex: 1, padding: "10px", borderRadius: 12, cursor: "pointer", textAlign: "center", border: gender === g ? "2px solid #6B4FBB" : "2px solid #EDE8FF", background: gender === g ? "#EDE8FF" : "white", fontWeight: gender === g ? 700 : 400, color: gender === g ? "#3D2B8E" : "#1A1035", fontSize: 14 }}>
                  {g}
                </div>
              ))}
            </div>
          </div>
          </div>
          <button style={btn()} onClick={() => setStep(1)}>Commencer →</button>
        </div>
      </div>
    </div>
  );

  if (step === 1) {
    const q = ADO_QUESTIONS[aq];
    const ans = adoAns[q.id] || [];
    const pct = Math.round(((aq + 1) / ADO_QUESTIONS.length) * 100);
    const ok = q.multi ? ans.length > 0 : ans.length === 1;
    return (
      <div style={{ background: "#F0F6FF", minHeight: "100vh", fontFamily: "system-ui" }}>
        <div style={{ background: `linear-gradient(135deg, ${V}, ${VM})`, padding: "20px 20px 16px", borderRadius: "0 0 20px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ ...sm, color: "rgba(255,255,255,0.6)" }}>Pour {name || "toi"} · {q.consigne}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{aq + 1}/{ADO_QUESTIONS.length}</div>
          </div>
          <div style={{ height: 5, background: "rgba(255,255,255,0.2)", borderRadius: 4 }}>
            <div style={{ height: "100%", background: G, borderRadius: 4, width: pct + "%", transition: "width 0.3s" }} />
          </div>
        </div>
        <div style={{ padding: 16 }}>
          <div style={card}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#0A1A2A", marginBottom: 6, lineHeight: 1.4 }}>{applyGenderQ(q.text, gender)}</div>
            <div style={{ fontSize: 12, color: "#4A8AB5", marginBottom: 16, fontStyle: "italic" }}>{q.consigne}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
              {q.options.map(o => {
                const sel = ans.includes(o.id);
                return (
                  <div key={o.id} onClick={() => ansAdo(q.id, o.id, q.multi, q.max)}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", borderRadius: 12, cursor: "pointer", border: sel ? `2px solid ${VM}` : `2px solid ${VP}`, background: sel ? VP : "#fff", transition: "all 0.15s" }}>
                    <span style={{ width: 20, height: 20, borderRadius: q.multi ? 4 : 100, border: sel ? "none" : "2px solid #90BAD4", background: sel ? VM : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, color: "#fff" }}>{sel ? "✓" : ""}</span>
                    <span style={{ fontSize: 14, fontWeight: sel ? 700 : 400, color: sel ? V : "#1A1035" }}>{gender === "Fille" ? (o.textF || o.text) : o.text}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button style={bto} onClick={prevA}>← Retour</button>
              <button style={{ ...btn(), opacity: !ok ? 0.4 : 1 }} onClick={nextA} disabled={!ok}>
                {aq < ADO_QUESTIONS.length - 1 ? "Suivant →" : "Terminer ma partie →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2) {
    const q = PARENT_QUESTIONS[pq];
    const ans = parAns[q.id] || [];
    const pct = Math.round(((pq + 1) / PARENT_QUESTIONS.length) * 100);
    const ok = ans.length > 0;
    const PC = "#5D4037", PL = "#EFEBE9";
    return (
      <div style={{ background: "#FBF8F5", minHeight: "100vh", fontFamily: "system-ui" }}>
        <div style={{ background: "linear-gradient(135deg, #4E342E, #6D4C41)", padding: "20px 20px 16px", borderRadius: "0 0 20px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "rgba(255,255,255,0.6)", textTransform: "uppercase" }}>Pour les parents · 3 questions seulement</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{pq + 1}/{PARENT_QUESTIONS.length}</div>
          </div>
          <div style={{ height: 5, background: "rgba(255,255,255,0.2)", borderRadius: 4 }}>
            <div style={{ height: "100%", background: G, borderRadius: 4, width: pct + "%", transition: "width 0.3s" }} />
          </div>
        </div>
        <div style={{ padding: 16 }}>
          <div style={card}>
            <div style={{ background: PL, borderLeft: "4px solid #A1887F", padding: "10px 14px", borderRadius: "0 8px 8px 0", fontSize: 12, color: "#5D4037", marginBottom: 16 }}>
              À 15-17 ans, votre enfant se connaît mieux que vous dans ce domaine. Vos 3 réponses complètent les siennes — ne les contredisez pas.
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#1A1035", marginBottom: 6, lineHeight: 1.4 }}>{applyGenderQ(q.text, gender)}</div>
            <div style={{ fontSize: 12, color: "#8D6E63", marginBottom: 16, fontStyle: "italic" }}>{q.consigne}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
              {q.options.map(o => {
                const sel = ans.includes(o.id);
                return (
                  <div key={o.id} onClick={() => ansPar(q.id, o.id, false, 1)}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 12, cursor: "pointer", border: sel ? `2px solid ${PC}` : `2px solid ${PL}`, background: sel ? PL : "#fff", transition: "all 0.15s" }}>
                    <span style={{ width: 20, height: 20, borderRadius: 100, border: sel ? "none" : "2px solid #BCAAA4", background: sel ? PC : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, color: "#fff" }}>{sel ? "✓" : ""}</span>
                    <span style={{ fontSize: 14, fontWeight: sel ? 700 : 400, color: sel ? "#3E2723" : "#1A1035" }}>{gender === "Fille" ? (o.textF || o.text) : o.text}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button style={{ ...bto, borderColor: "#BCAAA4", color: PC }} onClick={prevP}>← Retour</button>
              <button style={{ ...btn(PC), opacity: !ok ? 0.4 : 1 }} onClick={nextP} disabled={!ok}>
                {pq < PARENT_QUESTIONS.length - 1 ? "Suivant →" : "Voir le profil →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 3 && res) {
    const P = PROFILES[res.primary];
    const S = PROFILES[res.secondary];
    const TC = "#1B6B6B";
    return (
      <div style={{ fontFamily: "system-ui", background: "#F5FAFA", minHeight: "100vh" }}>
        <div style={{ background: `linear-gradient(135deg, ${P?.color || TC}, ${S?.color || TC})`, padding: "40px 20px 32px", borderRadius: "0 0 32px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>{P?.emoji}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", marginBottom: 4 }}>{P?.name}</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", marginBottom: 16 }}>Profil principal</div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.15)", borderRadius: 100, padding: "6px 16px" }}>
            <span>{S?.emoji}</span>
            <span style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>{S?.name} · profil secondaire</span>
          </div>
        </div>
        <div style={{ padding: "32px 20px", maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
          <div style={{ background: "#fff", borderRadius: 20, padding: "28px 24px", marginBottom: 20, boxShadow: "0 4px 24px rgba(27,107,107,0.08)" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0D2B2B", margin: "0 0 12px" }}>
              Votre rapport personnalisé est prêt
            </h2>
            <p style={{ fontSize: 14, color: "#4A3F72", lineHeight: 1.7, margin: "0 0 24px" }}>
              Le rapport PDF contient le profil complet, les 4 dimensions, les activités recommandées et les conseils aux parents.
            </p>
            <GenerateReport childName={name} childAge={age} gender={gender} primaryKey={res.primary} secondaryKey={res.secondary} scores={res.pct} questionnaire="D" />
          </div>
          <button style={{ background: "transparent", color: TC, border: "2px solid #1B6B6B", borderRadius: 100, padding: "12px 28px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "system-ui", marginTop: 8 }} onClick={onBack || (() => window.location.href = "/")}>
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return null;
}